import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { KeyRound, Lock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const errText = (detail, fallback) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return fallback;
};

// Blocking, non-dismissible gate. Whenever the signed-in user has a forced
// credential-change flag (set by an admin PIN/password reset), it forces them
// to set a new password and/or PIN before they can use the app.
export const ForceCredentialChange = () => {
  const { user, checkAuth } = useAuth();

  const needsPassword = !!user?.must_change_password;
  const needsPin = !!user?.must_change_pin;
  const open = needsPassword || needsPin;

  // Password step comes first when both are pending.
  const step = needsPassword ? 'password' : 'pin';

  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (pwd !== pwd2) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, { new_password: pwd }, { withCredentials: true });
      toast.success('Password updated');
      setPwd(''); setPwd2('');
      await checkAuth?.();
    } catch (err) {
      toast.error(errText(err.response?.data?.detail, 'Could not update password'));
    } finally { setSaving(false); }
  };

  const submitPin = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) { toast.error('PIN must be exactly 4 digits'); return; }
    if (pin !== pin2) { toast.error('PINs do not match'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/employee/change-pin`, { new_pin: pin }, { withCredentials: true });
      toast.success('PIN updated');
      setPin(''); setPin2('');
      await checkAuth?.();
    } catch (err) {
      toast.error(errText(err.response?.data?.detail, 'Could not update PIN'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-md bg-white [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="force-credential-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF3B6B]/10 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-5 h-5 text-[#FF3B6B]" />
            </div>
            <div>
              <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Set your new {step === 'password' ? 'password' : 'PIN'}
              </DialogTitle>
              <DialogDescription>
                Your {step === 'password' ? 'password' : 'PIN'} was reset by an admin. Please choose a new one to continue.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {step === 'password' ? (
          <form onSubmit={submitPassword} className="space-y-4" data-testid="force-password-form">
            <div>
              <Label htmlFor="fc-pwd" className="text-[#1E1E24] font-semibold">New password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                <Input id="fc-pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                  placeholder="At least 8 characters" required data-testid="force-password-input"
                  className="pl-11 rounded-xl border-[#E8E5DF]" />
              </div>
            </div>
            <div>
              <Label htmlFor="fc-pwd2" className="text-[#1E1E24] font-semibold">Confirm new password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                <Input id="fc-pwd2" type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                  placeholder="Re-enter password" required data-testid="force-password-confirm"
                  className="pl-11 rounded-xl border-[#E8E5DF]" />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="force-password-submit"
              className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-5">
              {saving ? 'Saving…' : 'Update password'}
            </Button>
          </form>
        ) : (
          <form onSubmit={submitPin} className="space-y-4" data-testid="force-pin-form">
            <div>
              <Label htmlFor="fc-pin" className="text-[#1E1E24] font-semibold">New 4-digit PIN</Label>
              <div className="relative mt-2">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                <Input id="fc-pin" type="password" inputMode="numeric" autoComplete="off" maxLength={4}
                  value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="••••" required data-testid="force-pin-input"
                  className="pl-11 rounded-xl border-[#E8E5DF] tracking-[0.4em] font-mono" />
              </div>
            </div>
            <div>
              <Label htmlFor="fc-pin2" className="text-[#1E1E24] font-semibold">Confirm new PIN</Label>
              <div className="relative mt-2">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
                <Input id="fc-pin2" type="password" inputMode="numeric" autoComplete="off" maxLength={4}
                  value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="••••" required data-testid="force-pin-confirm"
                  className="pl-11 rounded-xl border-[#E8E5DF] tracking-[0.4em] font-mono" />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="force-pin-submit"
              className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full py-5">
              {saving ? 'Saving…' : 'Update PIN'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForceCredentialChange;
